import type React from 'react'
import { useEffect, useState } from 'react'

import {
  hasLowerCase,
  hasMinLength,
  hasNumber,
  hasSpecialCharacter,
  hasUpperCase,
} from 'commons/utils/password'
import { Alert, Button, Icon, Password, Typography } from 'components'
import { ROUTES } from 'constants/urls'
import { AuthLayout } from 'layouts/AuthLayout'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  listSecurityParameters,
  type SecurityParameters,
  updatePasswordRequest,
} from 'services/forgetPassword'
import { clearState } from 'store/AuthContext/authSlice'
import { useDispatch } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import type { CodeResponseStorage } from './components/types/responses'
import * as S from './styles'

const { forgetPassword } = ROUTES

enum InputsNames {
  NEW = 'newPassword',
  CONFIRM = 'confirmPassword',
}

const IconCheck = {
  false: <Icon color={theme.colors.neutral.medium} icon="chevron-right" size="15" />,
  true: <Icon color={theme.colors.alert.success.dark} icon="task-alt" size="15" />,
}

type CodeStorage = { code: string } & CodeResponseStorage

const ResetPassword: React.FC = () => {
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [showPastPasswordError, setShowPastPasswordError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [securityParameters, setSecurityParameters] = useState<SecurityParameters[]>([])
  const navigate = useNavigate()

  const email = localStorage.getItem('resetPassword')
  const emailFromStorage: CodeStorage = email !== null && JSON.parse(email)

  const minLengthPassword = securityParameters.find((item) => item.cd_parm === 701)?.vl_parm || '8'

  useEffect(() => {
    if (emailFromStorage.return_chav !== 'Sucesso') {
      navigate(forgetPassword.getLink('validateCode'))
    }
    listSecurityParameters().then((response) => {
      setSecurityParameters(response)
    })
  }, [])

  useEffect(() => {
    if (
      newPassword === confirmPassword &&
      hasNumber(newPassword, securityParameters) &&
      hasSpecialCharacter(newPassword, securityParameters) &&
      hasUpperCase(newPassword, securityParameters) &&
      hasMinLength(newPassword, Number(minLengthPassword)) &&
      hasLowerCase(newPassword, securityParameters)
    ) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [newPassword, confirmPassword])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowPastPasswordError(false)
    const { name, value } = e.target
    if (name === InputsNames.NEW) {
      setNewPassword(value)
    } else if (name === InputsNames.CONFIRM) {
      setConfirmPassword(value)
    }
  }

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)

    try {
      const response = await updatePasswordRequest({
        ds_cod_senh: emailFromStorage.code,
        ds_confirm_senha: confirmPassword,
        ds_senh_nova: newPassword,
        id_usua: emailFromStorage.id_usua,
      })

      const { return_code } = response

      // NOTE: When the new password is the same as the old one
      if (return_code === 512) {
        setShowPastPasswordError(true)
      } else {
        dispatch(clearState())
        navigate(forgetPassword.getLink('changedPassword'))
      }
    } catch (error) {
      toast.error('Erro na alteração de senha')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <form autoComplete="true" onSubmit={handleResetPassword}>
        <S.FieldsWrapper>
          <Typography className="forgetTitle" design="hav-h5-text">
            Criar nova senha
          </Typography>

          <Typography className="forgetDescription">
            Muito bem, <strong>{emailFromStorage.nm_usua}</strong>!
            <br /> Agora você pode criar uma nova senha de acesso.
          </Typography>
          <Password
            errorOutline={showPastPasswordError}
            InputProps={{
              autoFocus: true,
              inputProps: { maxLength: 100 },
              placeholder: 'Insira seu e-mail',
            }}
            label="Nova senha"
            name={InputsNames.NEW}
            onChange={handleInputChange}
            placeholder=""
            value={newPassword}
          />
          <S.ValidateWrapper>
            <Typography className="validateTitle" design="hav-h4-text">
              Sua senha deve conter:
            </Typography>
            <div>
              <span className={hasNumber(newPassword, securityParameters) ? 'rulePassed' : ''}>
                {IconCheck[hasNumber(newPassword, securityParameters) ? 'true' : 'false']}
                Números
              </span>
              <span
                className={hasSpecialCharacter(newPassword, securityParameters) ? 'rulePassed' : ''}
              >
                {IconCheck[hasSpecialCharacter(newPassword, securityParameters) ? 'true' : 'false']}
                Caracteres especiais
              </span>
              <span className={hasUpperCase(newPassword, securityParameters) ? 'rulePassed' : ''}>
                {IconCheck[hasUpperCase(newPassword, securityParameters) ? 'true' : 'false']}
                Letras maiúsculas
              </span>
              <span
                className={hasMinLength(newPassword, Number(minLengthPassword)) ? 'rulePassed' : ''}
              >
                {IconCheck[hasMinLength(newPassword, Number(minLengthPassword)) ? 'true' : 'false']}
                Mínimo {minLengthPassword} caracteres
              </span>
              <span className={hasLowerCase(newPassword, securityParameters) ? 'rulePassed' : ''}>
                {IconCheck[hasLowerCase(newPassword, securityParameters) ? 'true' : 'false']}
                Letras minúsculas
              </span>
            </div>
          </S.ValidateWrapper>
          {showPastPasswordError && (
            <S.ErrorWrapper className="removeBigSpace">
              <Alert
                fullWidth
                kind="error"
                message="Você já utilizou essa senha anteriormente. Favor cadastrar uma senha diferente."
                title="Senha já utilizada."
              />
            </S.ErrorWrapper>
          )}
          <Password
            errorOutline={showPastPasswordError}
            InputProps={{
              autoFocus: true,
              inputProps: { maxLength: 100 },
              placeholder: 'Insira seu e-mail',
            }}
            label="Confirmar senha"
            name={InputsNames.CONFIRM}
            onChange={handleInputChange}
            placeholder=""
            value={confirmPassword}
          />
        </S.FieldsWrapper>
        <S.ButtonWrapper>
          <Button
            content="Resetar senha"
            disabled={isDisabled}
            fullWidth
            loading={loading}
            type="submit"
          />
        </S.ButtonWrapper>
      </form>
    </AuthLayout>
  )
}

export default ResetPassword
